import { useCallback, useMemo, useRef, useState } from "react";
import AsyncSelect from "react-select/async";
import { searchEmployees } from "../../attendance/rosterApi";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import {
  formatRoleLabel,
  isEmployeeOnlyRole,
  isManagerEligibleRole,
  normalizeEmployeeRole,
} from "../employeeRoleConstants";

const PAGE_SIZE = 50;

function formatOption(emp, roleOverride) {
  const code = emp.employee_code || "—";
  const name =
    emp.display_name ||
    [emp.first_name, emp.last_name].filter(Boolean).join(" ") ||
    "Unnamed";
  const desig = emp.designation?.title || emp.designation?.name || "";
  const dept = emp.department?.name || "";
  const role = normalizeEmployeeRole(roleOverride || emp.role);
  const secondary = [desig, dept, formatRoleLabel(role)].filter(Boolean).join(" · ");
  return {
    value: emp.employee_id,
    label: `${code} – ${name}${desig ? ` – ${desig}` : ""}`,
    meta: secondary,
    role,
    raw: emp,
  };
}

export default function ManagerEmployeeSelect({
  value,
  onChange,
  excludeEmployeeId,
  /** Extra ids to hide (e.g. the employee's own reports, to prevent circular lines). */
  excludeEmployeeIds,
  /** Optional (employeeId) => effective role, e.g. from the hierarchy tree. */
  resolveRole,
  disabled,
  placeholder = "Search by code, name, department…",
  /** When true (default), Employee-role people are hidden unless toggle is on. */
  preferEligibleManagers = true,
  compact = false,
}) {
  const debounceRef = useRef(null);
  const [pendingPick, setPendingPick] = useState(null);

  const loadOptions = useCallback(
    (inputValue) =>
      new Promise((resolve) => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
          try {
            const data = await searchEmployees({
              search: inputValue,
              page: 1,
              pageSize: PAGE_SIZE,
              activeOnly: true,
            });
            const items = Array.isArray(data?.items)
              ? data.items
              : Array.isArray(data)
                ? data
                : [];
            const candidates = items
              .filter((e) => !excludeEmployeeId || String(e.employee_id) !== String(excludeEmployeeId))
              .filter((e) => !excludeEmployeeIds || !excludeEmployeeIds.has(String(e.employee_id)));
            const roleOf = (e) => resolveRole?.(e.employee_id) || e.role;
            const toOption = (e) => formatOption(e, roleOf(e));
            if (!preferEligibleManagers) {
              resolve(candidates.map(toOption));
              return;
            }
            // Admin / HR / Manager first; Employee-role people follow in their own
            // group (picking one still asks for confirmation) so the list is never
            // empty in orgs where nobody has been given a manager role yet.
            const eligible = candidates.filter((e) => isManagerEligibleRole(roleOf(e))).map(toOption);
            const others = candidates.filter((e) => !isManagerEligibleRole(roleOf(e))).map(toOption);
            resolve(
              [
                { label: "Admin / HR / Manager", options: eligible },
                { label: "Other employees — confirmation needed", options: others },
              ].filter((g) => g.options.length)
            );
          } catch {
            resolve([]);
          }
        }, 300);
      }),
    [excludeEmployeeId, excludeEmployeeIds, resolveRole, preferEligibleManagers]
  );

  const selected = useMemo(() => {
    if (!value) return null;
    if (typeof value === "object" && value.value) return value;
    return { value, label: "Selected manager" };
  }, [value]);

  const handleChange = (opt) => {
    if (!opt) {
      onChange?.(null);
      return;
    }
    if (preferEligibleManagers && isEmployeeOnlyRole(opt.role ?? opt.raw?.role)) {
      setPendingPick(opt);
      return;
    }
    onChange?.(opt);
  };

  const confirmEmployeeAsManager = () => {
    if (pendingPick) onChange?.(pendingPick);
    setPendingPick(null);
  };

  const pendingName =
    pendingPick?.raw?.display_name ||
    [pendingPick?.raw?.first_name, pendingPick?.raw?.last_name].filter(Boolean).join(" ") ||
    pendingPick?.label ||
    "This person";
  const pendingCode = pendingPick?.raw?.employee_code;

  return (
    <div className="space-y-2">
      <AsyncSelect
        key={`mgr-${excludeEmployeeId || ""}`}
        noOptionsMessage={() => "No matching employee"}
        cacheOptions
        defaultOptions
        loadOptions={loadOptions}
        value={selected}
        onChange={handleChange}
        isClearable
        isDisabled={disabled}
        placeholder={placeholder}
        formatOptionLabel={(opt) => (
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-900">{opt.label}</span>
              {opt.role ? (
                <span
                  className={[
                    "text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded",
                    isEmployeeOnlyRole(opt.role)
                      ? "bg-amber-50 text-amber-700"
                      : "bg-sky-50 text-sky-700",
                  ].join(" ")}
                >
                  {formatRoleLabel(opt.role)}
                </span>
              ) : null}
            </div>
            {opt.meta ? <div className="text-xs text-gray-500">{opt.meta}</div> : null}
          </div>
        )}
        classNamePrefix="react-select"
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        menuPosition="fixed"
        styles={{
          control: (base) => ({
            ...base,
            minHeight: 36,
            borderColor: "#e2e8f0",
            borderRadius: 8,
          }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }}
      />

      {preferEligibleManagers && !compact ? (
        <p className="text-[11px] text-slate-500">
          Admin, HR and Manager roles are listed first. Picking an Employee-role person asks for confirmation.
        </p>
      ) : null}

      <ConfirmDialog
        open={Boolean(pendingPick)}
        title="Make this Employee a reporting manager?"
        message={
          pendingCode
            ? `${pendingName} (${pendingCode}) has the Employee role (not Admin, HR or Manager). Do you still want to make this person a reporting manager?`
            : `${pendingName} has the Employee role (not Admin, HR or Manager). Do you still want to make this person a reporting manager?`
        }
        confirmLabel="Yes, use as manager"
        cancelLabel="No, pick someone else"
        onConfirm={confirmEmployeeAsManager}
        onCancel={() => setPendingPick(null)}
      />
    </div>
  );
}
